import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, Check } from 'lucide-react';
import { usePrivacyMode } from '../../contexts/PrivacyModeContext';
import PrivacyModeOffDialog from '../PrivacyModeOffDialog';

/**
 * PrivacyModeCard — big-switch that lives on the "Your Privacy" tab.
 * Same behaviour as the header shield: one-tap ON, password-gated OFF.
 *
 * Deliberately styled larger than a normal setting row (2× toggle,
 * emerald pulse when active) so members feel a small physical
 * satisfaction flipping it.
 */
const WHAT_IT_DOES = [
  'Amounts hidden',
  'Asset names blurred',
  'Browser tab disguised',
  'Charts blurred',
];

const PrivacyModeCard = () => {
  const { privacyMode, turnOn, turnOff } = usePrivacyMode();
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleChange = (next) => {
    if (next) turnOn();
    else setDialogOpen(true);
  };

  return (
    <>
      <Card data-testid="privacy-mode-card" className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className={`w-5 h-5 ${privacyMode ? 'text-emerald-600' : 'text-slate-400'}`} aria-hidden="true" />
            <span>Privacy Mode</span>
            {privacyMode && (
              <Badge
                data-testid="privacy-mode-active-badge"
                className="ml-1 bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100 inline-flex items-center gap-1.5"
              >
                <span
                  className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"
                  aria-hidden="true"
                  data-testid="privacy-mode-pulse-dot"
                />
                On
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            One switch that quiets the whole app for public places. Your Code Name shows as <em>MEMBER</em> and the tier badge is hidden.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ul className="grid grid-cols-2 gap-x-4 gap-y-2" data-testid="privacy-mode-checklist">
            {WHAT_IT_DOES.map((item) => (
              <li key={item} className="flex items-center gap-2 text-sm text-slate-700">
                <span className="w-4 h-4 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <Check className="w-2.5 h-2.5 text-emerald-700" aria-hidden="true" />
                </span>
                {item}
              </li>
            ))}
          </ul>
          <div className="flex items-center justify-between gap-4 pt-2 border-t border-slate-100">
            <p className="text-sm text-slate-600">
              {privacyMode
                ? 'Turning it off will ask for your password.'
                : 'One tap turns it on. Turning it off asks for your password.'}
            </p>
            <Switch
              checked={privacyMode}
              onCheckedChange={handleChange}
              data-testid="privacy-mode-switch"
              className="scale-125"
              aria-label={privacyMode ? 'Turn Privacy Mode off' : 'Turn Privacy Mode on'}
            />
          </div>
        </CardContent>
      </Card>
      <PrivacyModeOffDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={turnOff}
      />
    </>
  );
};

export default PrivacyModeCard;
